import { Box, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "../../../../theme/tokens";
import MetaChip from "../../../UIToolkit/Badges/MetaChip";
import { EyebrowLabel } from "../../../UIToolkit/molecules/EyebrowLabel";
import { getStaffingStatusStyle } from "../../WeeklyGapView/CountBadge";
import { formatStaffCount, type GapsShiftGroup, isShownAsShort } from "../gapsDayModel";
import { coverPromptFor } from "../gapsGroupSheets";
import { GapsQuantityHero, QUANTITY_HERO_FIGURE_SX } from "./GapsQuantityHero";

type Props = {
	/** The group the post is for, read live — `undefined` for a post started from nothing. */
	group: GapsShiftGroup | undefined;
	neededNurses: number;
	onNeededNursesChange: (next: number) => void;
	disabled: boolean;
};

const SHEET = "gaps-post-shift";

/**
 * The post sheet's one focal moment: HOW MANY, as a big stepper, beside the window it is answering — the group's own
 * `filled/required` and its "N short · M posted" as the home prints them (`coverPromptFor`, not netted).
 *
 * The card (`GapsQuantityHero`, the posted sheet's "staff needed" card too) takes the window's ink as a whisper of a
 * wash, as the cover prompt's does: red while the window is short, so "post two" reads against "two short"; the brand's
 * when it is not, or when the post was started from nothing.
 */
export const GapsPostHero: FC<Props> = ({ group, neededNurses, onNeededNursesChange, disabled }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const cover = group === undefined ? undefined : coverPromptFor(group);
	const isShort = cover !== undefined && isShownAsShort(cover.short);
	const staffing = group === undefined ? undefined : getStaffingStatusStyle(group.actual, group.required);
	const ink = isShort ? theme.palette.error.main : theme.palette.primary.main;

	return (
		<GapsQuantityHero
			ink={ink}
			dataTestId={`${SHEET}-hero`}
			label={t("gaps.postSheet.howMany")}
			stepperLabel={t("gaps.postSheet.howManyLabel")}
			value={neededNurses}
			onChange={onNeededNursesChange}
			disabled={disabled}
			stepperDataTestId={`${SHEET}-quantity`}
			asideDataTestId={`${SHEET}-context`}
			aside={
				group !== undefined && cover !== undefined && staffing !== undefined ? (
					<>
						<EyebrowLabel>{t("gaps.postSheet.nowLabel", { job: group.jobLabel, time: group.timeLabel })}</EyebrowLabel>
						<Typography sx={{ ...QUANTITY_HERO_FIGURE_SX, color: INK.PRIMARY }}>
							{/* The desktop's own inks for this same cell: flagged red from two short, amber when over. */}
							<Box component="span" sx={{ color: staffing.isFlagged ? staffing.color : INK.PRIMARY }}>
								{formatStaffCount(group.actual)}
							</Box>
							<Box component="span" sx={{ color: INK.MUTED, fontWeight: 600 }}>{`/${formatStaffCount(group.required)}`}</Box>
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.5, mt: 0.75 }}>
							<MetaChip
								size="small"
								label={isShort ? t("gaps.callIn.cover.short", { count: Number(formatStaffCount(cover.short)) }) : t("gaps.callIn.cover.covered")}
								tone={isShort ? "error" : "success"}
								dataTestId={`${SHEET}-short`}
							/>
							{cover.posted > 0 && (
								<MetaChip size="small" label={t("gaps.group.posted", { count: cover.posted })} tone="success" dataTestId={`${SHEET}-posted`} />
							)}
						</Box>
					</>
				) : undefined
			}
		/>
	);
};
